import 'react-popper-tooltip/dist/styles.css';

import { useEffect, useMemo, useRef } from 'react';

import classNames from 'classnames';
import { createPortal } from 'react-dom';
import { usePopperTooltip } from 'react-popper-tooltip';

import { MIN_SPACE_FOR_ARROW, TOOLTIP_ARROW_HEIGHT, TOOLTIP_ARROW_WIDTH, TOOLTIP_TOP_OFFSET } from './constants';
import type { PopperTooltipOffsetsFunctionArgs, RedesignPopperTooltipProps } from './types';
import styles from './UiRedesignPopperTooltip.module.scss';

export const getOffsetConfig = ({
  offset,
  placement,
}: PopperTooltipOffsetsFunctionArgs): [number, number] | undefined => {
  const xOffset = offset ? offset[0] : 0;
  switch (placement) {
    case 'bottom-end':
      return [xOffset < MIN_SPACE_FOR_ARROW ? MIN_SPACE_FOR_ARROW : xOffset, TOOLTIP_TOP_OFFSET];
    case 'bottom-start':
      return [xOffset > -MIN_SPACE_FOR_ARROW ? -MIN_SPACE_FOR_ARROW : xOffset, TOOLTIP_TOP_OFFSET];
    case 'auto-end':
    case 'top-end':
    case 'right-end':
    case 'left-end':
    case 'bottom':
    case 'left':
    case 'left-start':
    case 'right':
    case 'right-start':
    default:
      return offset;
  }
};

export function UiRedesignPopperTooltip(props: RedesignPopperTooltipProps) {
  const {
    dataTestId,
    className,
    wrapperClassName,
    childrenClassName,
    isBig,
    content,
    children,
    handleEvents,
    trigger = 'click',
    placement = 'bottom-end',
    offset,
    container,
    disabled = false,
  } = props;

  const setTriggerRefContainer = useRef(null);

  const config = useMemo(() => {
    const configuredOffset = getOffsetConfig({ placement, offset });
    return {
      trigger,
      placement,
      offset: configuredOffset,
    };
  }, [trigger, offset, placement]);

  const { getArrowProps, getTooltipProps, setTooltipRef, setTriggerRef, visible } = usePopperTooltip(config);

  const baseContainerClassName = isBig ? 'tooltip-container tooltip-container--is-big' : 'tooltip-container';

  const tooltipClassname = classNames(baseContainerClassName, className, {
    [styles.tooltipContainer]: true,
    [styles.tooltipContainerBig]: isBig,
  });

  useEffect(() => {
    if (visible && handleEvents) {
      handleEvents();
    }
  }, [visible, handleEvents]);

  return (
    <div className={wrapperClassName}>
      <div ref={setTriggerRef} className={classNames(styles.tooltipIcon, childrenClassName)} data-testid={dataTestId}>
        {children}
      </div>
      {visible &&
        !disabled &&
        createPortal(
          <div
            ref={setTooltipRef}
            {...getTooltipProps({
              className: tooltipClassname,
            })}
          >
            <div ref={setTriggerRefContainer}>{content}</div>
            <div>
              <div
                {...getArrowProps({
                  className: classNames('tooltip-arrow', styles.tooltipArrow),
                  style: {
                    '--tooltipBackground': '#BC3AFA',
                    width: TOOLTIP_ARROW_WIDTH,
                    height: TOOLTIP_ARROW_HEIGHT,
                    top: -(TOOLTIP_ARROW_HEIGHT + TOOLTIP_TOP_OFFSET),
                  },
                })}
              />
            </div>
          </div>,
          container || document.body,
        )}
    </div>
  );
}
